import { useCallback, useMemo } from "react"
import type { SearchResult } from "@/schema/search"

import { useFetchPortfolioCustodianAccounts } from "@/hooks/api/clients"
import { useFetchHouseholdFinancialAccounts } from "@/hooks/api/financial-accounts/queries"
import { useFetchHouseholdInsurancePolicies } from "@/hooks/api/insurance-policies/queries"

function matchesQuery(label: string, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  return label.toLowerCase().includes(normalizedQuery)
}

export function useHouseholdNoteMentionables(householdId: string | null | undefined) {
  const enabled = !!householdId

  const custodianQuery = useFetchPortfolioCustodianAccounts(householdId ?? null, { enabled })
  const financialQuery = useFetchHouseholdFinancialAccounts(householdId ?? "", { enabled })
  const insuranceQuery = useFetchHouseholdInsurancePolicies(householdId ?? "")

  const mentionables = useMemo<SearchResult[]>(() => {
    if (!householdId) return []

    const results: SearchResult[] = []

    for (const account of custodianQuery.data ?? []) {
      if (!account.id) continue
      const displayLabel = [account.name, account.masked_account_number].filter(Boolean).join(" ")
      results.push({
        id: account.id,
        type: "custodian_account",
        match_type: "text",
        score: 100,
        display_label: displayLabel || "Custodian account",
        attributes: {
          custodian: account.custodian ?? undefined,
          masked_account_number: account.masked_account_number ?? undefined,
          account_type: account.account_type_normalized ?? undefined,
        },
      })
    }

    for (const account of financialQuery.data ?? []) {
      // Only manual (non-BridgeFT) accounts — synced investment accounts surface via custodian accounts.
      if (account.bridgeft_id) continue
      const displayLabel = [account.name, account.account_number].filter(Boolean).join(" ")
      results.push({
        id: account.id,
        type: "financial_account",
        match_type: "text",
        score: 100,
        display_label: displayLabel || "Account",
        attributes: {
          institution_name: account.institution_name,
          masked_account_number: account.account_number,
          account_type: account.account_type,
        },
      })
    }

    for (const policy of insuranceQuery.data ?? []) {
      const displayLabel = [policy.name, policy.carrier, policy.policy_number].filter(Boolean).join(" — ")
      results.push({
        id: policy.id,
        type: "insurance_policy",
        match_type: "text",
        score: 100,
        display_label: displayLabel || "Insurance policy",
        attributes: {
          carrier: policy.carrier ?? undefined,
          masked_policy_number: policy.policy_number ?? undefined,
          insurance_type: policy.insurance_type,
        },
      })
    }

    return results
  }, [householdId, custodianQuery.data, financialQuery.data, insuranceQuery.data])

  const filterMentionables = useCallback(
    (query: string) => mentionables.filter((result) => matchesQuery(result.display_label, query)),
    [mentionables]
  )

  return {
    mentionables,
    isLoading: enabled && (custodianQuery.isLoading || financialQuery.isLoading || insuranceQuery.isLoading),
    filterMentionables,
  }
}
